import { useEffect, useState } from 'react';
import api, { ApiError } from '../../../lib/apiClient';
import { IconButton } from '../../../components/IconButton';
import { IconDownload } from '../../../components/icons';
import { useConfig } from '../../../context/ConfigContext';
import { downloadFile } from '../../../utils/download';
import { formatMoney } from '../../../utils/format';

interface BankAccount {
  id: number;
  name: string;
}

interface ReportRow {
  transaction_date: string;
  description: string;
  reference: string | null;
  amount: number;
  status: string;
}

interface ReportSummaryItem {
  label: string;
  value: number;
  type: 'money' | 'percent' | 'count';
}

interface ReportPage {
  data: ReportRow[];
  summary: ReportSummaryItem[] | null;
}

export function BankReconciliationReport() {
  const { settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<ReportPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get<BankAccount[]>('/bank-accounts').then((res) => {
      setBankAccounts(res.data);
      if (res.data[0]) setBankAccountId(String(res.data[0].id));
    });
  }, []);

  useEffect(() => {
    if (!bankAccountId) return;
    setLoading(true);
    setError('');
    api
      .get<ReportPage>('/reports/banking/reconciliation', { params: { bank_account_id: bankAccountId, as_of: asOf } })
      .then((res) => setData(res.data))
      .catch((err: ApiError) => setError(err.message || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, [bankAccountId, asOf]);

  const exportFile = async (kind: 'pdf' | 'xlsx') => {
    setExporting(true);
    try {
      await downloadFile(api, `/reports/banking/reconciliation/${kind}`, `bank-reconciliation.${kind}`, {
        bank_account_id: bankAccountId,
        as_of: asOf,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Bank Reconciliation</h1>
      </header>
      <p className="muted">
        Balance per the General Ledger vs. balance per cleared statement items, as of a date, with every unmatched or
        not-yet-cleared statement line listed as a reconciling item.
      </p>

      <div className="toolbar">
        <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
          {bankAccounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <label className="checkbox-inline">
          As of
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </label>
        <IconButton icon={IconDownload} label={exporting ? 'Exporting…' : 'Export PDF'} onClick={() => exportFile('pdf')} disabled={exporting} />
        <IconButton icon={IconDownload} label={exporting ? 'Exporting…' : 'Export Excel'} onClick={() => exportFile('xlsx')} disabled={exporting} />
      </div>

      {data?.summary && (
        <div className="stat-grid">
          {data.summary.map((s) => (
            <div className="card stat" key={s.label}>
              <span className="stat-label">{s.label}</span>
              <span className="stat-value">{s.type === 'money' ? fmtMoney(s.value) : s.value}</span>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Reference</th>
                <th className="right">Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((row, i) => (
                  <tr key={i}>
                    <td>{row.transaction_date}</td>
                    <td>{row.description}</td>
                    <td className="muted">{row.reference ?? '—'}</td>
                    <td className="right">{fmtMoney(row.amount)}</td>
                    <td>{row.status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="muted center">
                    No outstanding reconciling items.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
